/**
 * waap-logs-tab.tsx — Read-only Log Archiving tab for a WAAP service.
 *
 * Shows the account's log-archiving configuration list and the available
 * OSS buckets. Create/edit config is deliberately not wired (vendor bucket
 * API broken on the Partner side too — spec confirms read-only for B1).
 *
 * Data sources:
 *   GET /waap/domains/:anchorId/log-archive/configs  (useActAsWAAPLogArchiveConfigs)
 *   GET /waap/domains/:anchorId/log-archive/buckets  (useActAsWAAPLogArchiveBuckets)
 */

import { useTranslation } from 'react-i18next';
import { Archive } from 'lucide-react';
import { Button, Card, CardBody, CardHeader, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useActAsWAAPLogArchiveConfigs,
  useActAsWAAPLogArchiveBuckets,
  type ActAsWAAPLogArchiveConfig,
  type ActAsWAAPOSSBucket,
} from '@/api/act-as-waap';

/* ── Column definitions — Log Archive Configs ────────────────────────────── */

const configColumns: ColumnDef<ActAsWAAPLogArchiveConfig, unknown>[] = [
  {
    id: 'id',
    header: 'ID',
    cell: ({ row }) => (
      <span style={{ fontFamily: 'monospace', fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {String(row.original.id ?? '—')}
      </span>
    ),
  },
  {
    id: 'raw',
    header: 'Config',
    cell: ({ row }) => {
      // Field names not confirmed by vendor yet — render raw JSON best-effort.
      const { id: _id, ...rest } = row.original;
      void _id;
      const preview = JSON.stringify(rest, null, 0);
      return (
        <span
          title={preview}
          style={{
            fontFamily: 'monospace',
            fontSize: 'var(--vn-font-size-xs)',
            color: 'var(--vn-fg-default)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            maxWidth: 480,
          }}
        >
          {preview}
        </span>
      );
    },
  },
];

/* ── Column definitions — OSS Buckets ───────────────────────────────────── */

const bucketColumns: ColumnDef<ActAsWAAPOSSBucket, unknown>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Bucket Name',
    cell: ({ row }) => (
      <span style={{ fontWeight: 500, color: 'var(--vn-fg-default)', wordBreak: 'break-all' }}>
        {row.original.name}
      </span>
    ),
  },
  {
    id: 'creation_date',
    header: 'Created',
    cell: ({ row }) => (
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-subtle)' }}>
        {row.original.creation_date
          ? new Date(row.original.creation_date * 1000).toLocaleDateString()
          : '—'}
      </span>
    ),
  },
];

/* ── Component ───────────────────────────────────────────────────────────── */

interface WaapLogsTabProps {
  anchorId: string;
}

/**
 * Logs tab — WAAP log archiving configurations and OSS bucket list (read-only).
 *
 * Add/edit archive config is intentionally disabled: the vendor's bucket API
 * is broken on the server side (same constraint in Partner console).
 */
export function WaapLogsTab({ anchorId }: WaapLogsTabProps) {
  const { t } = useTranslation();
  const configsQ = useActAsWAAPLogArchiveConfigs(anchorId);
  const bucketsQ = useActAsWAAPLogArchiveBuckets(anchorId);

  const isLoading = configsQ.isLoading || bucketsQ.isLoading;
  const isError   = configsQ.isError   || bucketsQ.isError;

  if (isError) {
    return (
      <Alert
        tone="danger"
        title={t('waap_domain.logs_load_error', 'Failed to load log archive data')}
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { void configsQ.refetch(); void bucketsQ.refetch(); }}
          >
            {t('common.retry', 'Retry')}
          </Button>
        }
      />
    );
  }

  if (isLoading) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} style={{ height: 32 }} />
        ))}
      </div>
    );
  }

  const configs = configsQ.data ?? [];
  const buckets = bucketsQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-5)' }}>
      {/* Note: Create/edit archive config is disabled — vendor API broken */}
      <Alert tone="info">
        {t(
          'waap_domain.logs_readonly_note',
          "Log archive configuration is read-only. Create/edit is not available — the vendor's bucket write API is currently unavailable.",
        )}
      </Alert>

      {/* ── Log Archive Configs ──────────────────────────────────────── */}
      <Card>
        <CardHeader
          actions={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => configsQ.refetch()}
              disabled={configsQ.isFetching}
            >
              {configsQ.isFetching
                ? t('waap_domain.refreshing', 'Refreshing…')
                : t('waap_domain.refresh', 'Refresh')}
            </Button>
          }
        >
          <CardTitle>{t('waap_domain.logs_configs_title', 'Log Archive Configurations')}</CardTitle>
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          <DataTable
            columns={configColumns}
            data={configs}
            getRowId={(r) => String(r.id ?? Math.random())}
            dense
            emptyState={
              <EmptyState
                icon={<Archive size={24} />}
                title={t('waap_domain.logs_configs_empty', 'No log archive configurations')}
                description={t(
                  'waap_domain.logs_configs_empty_desc',
                  'No log archiving has been configured for this WAAP service.',
                )}
              />
            }
            footer={
              configs.length > 0
                ? `${configs.length} config${configs.length !== 1 ? 's' : ''}`
                : undefined
            }
          />
        </CardBody>
      </Card>

      {/* ── OSS Buckets ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t('waap_domain.logs_buckets_title', 'OSS Buckets')}</CardTitle>
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          <DataTable
            columns={bucketColumns}
            data={buckets}
            getRowId={(r) => r.name}
            dense
            emptyState={
              <EmptyState
                icon={<Archive size={24} />}
                title={t('waap_domain.logs_buckets_empty', 'No OSS buckets')}
                description={t(
                  'waap_domain.logs_buckets_empty_desc',
                  'No OSS buckets found for this WAAP service account.',
                )}
              />
            }
            footer={
              buckets.length > 0
                ? `${buckets.length} bucket${buckets.length !== 1 ? 's' : ''}`
                : undefined
            }
          />
        </CardBody>
      </Card>
    </div>
  );
}
